import { zodResolver } from '@hookform/resolvers/zod';
import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { ArrowLeftOutline, Button, Display, HStack, Stack, useToast } from '@bonusly/design-system';

import { PageTitle } from '../../../components/page/page-title';
import { FORM_PANEL_WIDTH } from '../../../config/admin';
import { GroupType } from '../../../entities/groups';
import { ClaimLimitPeriod, IncentiveVisibility } from '../../../entities/incentives';
import { useCreateIncentive } from '../../../queries/incentives';
import { ClaimLimitMode, IncentiveFormData } from '../types';
import { IncentiveForm } from './incentive-form';
import { useSchema } from './use-schema';
import { buildFormBody } from './utils';

export const CreateIncentive: FC = () => {
	const { t } = useTranslation('incentives', { keyPrefix: 'create' });
	const toast = useToast();
	const navigate = useNavigate();
	const schema = useSchema();

	const form = useForm<IncentiveFormData>({
		defaultValues: {
			amount: undefined,
			name: '',
			icon: null,
			description: '',
			requireProof: false,
			message: '',
			conditionsOperator: GroupType.Or,
			conditions: [],
			claimLimitMode: ClaimLimitMode.Unlimited,
			claimLimitQuantity: 1,
			periodType: ClaimLimitPeriod.Month,
			userClaimLimit: null,
			visibility: IncentiveVisibility.Public,
			requireApproval: false,
			approvalLevels: [[]],
		},
		resolver: zodResolver(schema),
	});

	const { mutate, isLoading } = useCreateIncentive({
		onMutate: () => {},
		onSuccess: () => {
			toast({
				title: t('createSuccess'),
				status: 'success',
			});
			navigate('/admin/programs/incentives');
		},
		onError: () => {
			toast({
				title: t('createFailure'),
				status: 'error',
			});
		},
	});

	return (
		<>
			<PageTitle>{t('title')}</PageTitle>

			<Stack px={10} py={8} spacing={6} maxW={FORM_PANEL_WIDTH}>
				<HStack>
					<Button as={Link} variant="link" gap={1} to="/admin/programs/incentives" type="button">
						<ArrowLeftOutline size="xs" />
						{t('back')}
					</Button>
				</HStack>

				<Display size="sm">{t('title')}</Display>

				<IncentiveForm form={form} isLoading={isLoading} onSubmit={(data) => mutate(buildFormBody(data))}>
					<HStack justify="flex-end">
						<Button type="submit" isLoading={isLoading}>
							{t('submitLabel')}
						</Button>
					</HStack>
				</IncentiveForm>
			</Stack>
		</>
	);
};
